import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

export const ACTIONS = {
  accesso: "Accesso", accesso_fallito: "Accesso fallito", accesso_bloccato: "Accesso bloccato", uscita: "Uscita",
  password_cambiata: "Password cambiata", password_reimpostata: "Password reimpostata", "2fa_attivata": "2 passaggi attivata",
  "2fa_disattivata": "2 passaggi disattivata", "2fa_azzerata": "2 passaggi azzerata", utente_creato: "Utente creato",
  utente_eliminato: "Utente eliminato", ruolo_cambiato: "Ruolo cambiato", impostazioni_modificate: "Impostazioni modificate",
  documento_aperto: "Documento aperto", documento_negato: "Documento negato", pdf_contratto: "PDF contratto",
  pdf_impegnativa: "PDF impegnativa", pdf_incassi: "PDF incassi", contratto_firmato: "Contratto firmato",
  impegnativa_firmata: "Impegnativa firmata", impegnativa_stato: "Stato impegnativa", appuntamento_eliminato: "Appuntamento eliminato",
  incasso_eliminato: "Incasso eliminato", impegno_eliminato: "Impegno eliminato", messaggio_eliminato: "Messaggio eliminato",
  privacy_esportazione: "Esportazione dati", privacy_anonimizzazione: "Anonimizzazione", backup_scaricato: "Backup scaricato",
};
const WARN = new Set(["accesso_fallito", "accesso_bloccato", "documento_negato", "utente_eliminato", "ruolo_cambiato", "privacy_anonimizzazione", "2fa_disattivata", "2fa_azzerata"]);

function Row({ e }) {
  return (
    <li className="py-3 grid sm:grid-cols-[150px_170px_1fr] gap-1 sm:gap-4 text-sm" data-testid="audit-row">
      <span className="text-[var(--muted)] tabular-nums">{new Date(e.at).toLocaleString("it-IT")}</span>
      <span className={`font-semibold ${WARN.has(e.action) ? "text-red-600" : ""}`}>{ACTIONS[e.action] || e.action}</span>
      <span className="min-w-0 break-words">
        <b>{e.user_name || e.target || "—"}</b> {e.detail}
        <span className="block text-xs text-[var(--muted)]">IP {e.ip || "—"}</span>
      </span>
    </li>
  );
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: "", action: "", start: "", end: "" });
  const load = useCallback(async () => {
    try { setRows((await api.get("/audit", { params: f })).data); } catch (e) { toast.error(formatApiError(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="fade-up">
        <p className="eyebrow">Sicurezza</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Registro attività</h1>
        <p className="text-sm text-[var(--muted)] mt-2">Accessi, documenti aperti, firme, cancellazioni e modifiche. Le voci non si possono modificare né cancellare dall'app.</p>
      </div>
      <div className="grid sm:grid-cols-[1fr_200px_150px_150px] gap-3">
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-[var(--muted)]" /><Input className="pl-9" placeholder="Cerca persona, IP o dettaglio" value={f.q} onChange={set("q")} data-testid="audit-search-input" /></div>
        <select value={f.action} onChange={set("action")} className="h-10 rounded-md border border-[var(--border)] px-3 text-sm bg-white" data-testid="audit-action-select">
          <option value="">Tutte le azioni</option>
          {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input type="date" value={f.start} onChange={set("start")} data-testid="audit-start-input" />
        <Input type="date" value={f.end} onChange={set("end")} data-testid="audit-end-input" />
      </div>
      <ul className="rounded-2xl bg-white border border-[var(--border)] px-5 divide-y divide-[var(--border)]" data-testid="audit-list">
        {rows.map((e, i) => <Row key={`${e.at}-${i}`} e={e} />)}
        {!rows.length && <li className="py-10 text-center text-[var(--muted)]" data-testid="audit-empty">Nessuna attività trovata</li>}
      </ul>
    </div>
  );
}
